import { useParams, Link } from 'react-router-dom';
import { articleMap, articles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

const articleImageUrls: Record<string, string> = {
  'anwang91-latest-access-address': 'https://readdy.ai/api/search-image?query=A%20dark%20digital%20tunnel%20with%20glowing%20onion%20layers%20representing%20Tor%20network%20encryption%2C%20abstract%20cybersecurity%20concept%2C%20warm%20amber%20and%20dark%20tones%2C%20minimalist%20digital%20art%20style&width=800&height=400&seq=article-1&orientation=landscape',
  'tor-browser-install-guide': 'https://readdy.ai/api/search-image?query=A%20clean%20minimalist%20digital%20workspace%20showing%20Tor%20browser%20interface%20concept%2C%20soft%20green%20and%20warm%20lighting%2C%20modern%20tech%20aesthetic%2C%20abstract%20privacy%20protection%20theme&width=800&height=400&seq=article-2&orientation=landscape',
  'anwang91-registration-guide': 'https://readdy.ai/api/search-image?query=An%20abstract%20digital%20registration%20interface%20with%20lock%20icons%20and%20shield%20symbols%2C%20warm%20amber%20accents%20on%20dark%20background%2C%20cybersecurity%20aesthetic%2C%20clean%20modern%20design&width=800&height=400&seq=article-3&orientation=landscape',
  'darknet-trading-security': 'https://readdy.ai/api/search-image?query=Abstract%20digital%20transaction%20security%20concept%20with%20shield%20and%20crypto%20symbols%2C%20warm%20green%20and%20amber%20tones%2C%20modern%20minimalist%20financial%20security%20design&width=800&height=400&seq=article-4&orientation=landscape',
  'identify-darknet-scams': 'https://readdy.ai/api/search-image?query=A%20warning%20sign%20concept%20with%20digital%20phishing%20hook%20and%20shield%2C%20warm%20red%20and%20amber%20accents%2C%20cybersecurity%20awareness%20design%2C%20clean%20minimalist%20style&width=800&height=400&seq=article-5&orientation=landscape',
  'darknet-malware-defense': 'https://readdy.ai/api/search-image?query=Digital%20virus%20defense%20concept%20with%20shield%20blocking%20malicious%20code%2C%20warm%20green%20glow%2C%20cybersecurity%20protection%20aesthetic%2C%20modern%20minimalist%20tech%20design&width=800&height=400&seq=article-6&orientation=landscape',
  'anonymous-communication-best-practices': 'https://readdy.ai/api/search-image?query=Abstract%20anonymous%20communication%20concept%20with%20encrypted%20data%20streams%20flowing%20through%20layered%20nodes%2C%20warm%20amber%20and%20green%20tones%2C%20modern%20privacy%20design&width=800&height=400&seq=article-7&orientation=landscape',
  'data-encryption-techniques': 'https://readdy.ai/api/search-image?query=Digital%20encryption%20visualization%20with%20data%20transforming%20into%20encrypted%20blocks%2C%20warm%20green%20and%20dark%20tones%2C%20modern%20cryptography%20aesthetic%2C%20minimalist%20tech%20design&width=800&height=400&seq=article-8&orientation=landscape',
  'anwang91-platform-review': 'https://readdy.ai/api/search-image?query=A%20professional%20platform%20review%20dashboard%20concept%20with%20star%20ratings%20and%20analytics%2C%20warm%20amber%20accents%2C%20modern%20business%20review%20aesthetic%2C%20clean%20design&width=800&height=400&seq=article-9&orientation=landscape',
  'darknet-market-comparison': 'https://readdy.ai/api/search-image?query=A%20comparison%20chart%20concept%20showing%20multiple%20platforms%20side%20by%20side%2C%20warm%20amber%20and%20green%20highlights%2C%20modern%20analytics%20design%2C%20minimalist%20data%20comparison%20aesthetic&width=800&height=400&seq=article-10&orientation=landscape',
  'darknet-security-trends-2026': 'https://readdy.ai/api/search-image?query=Futuristic%20cybersecurity%20threat%20landscape%20with%20AI%20nodes%20and%20quantum%20patterns%2C%20warm%20amber%20warning%20accents%2C%20modern%20data%20visualization%20design&width=800&height=400&seq=article-11&orientation=landscape',
  'global-darknet-regulation-updates': 'https://readdy.ai/api/search-image?query=Global%20regulation%20concept%20with%20world%20map%20and%20legal%20documents%2C%20warm%20green%20and%20amber%20tones%2C%20modern%20policy%20analysis%20design&width=800&height=400&seq=article-12&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center px-4">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
            </span>
            <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">
              文章未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              您访问的文章不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 text-sm"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categoryMap[article.category];
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    url: `https://www.anwang91.com/article/${slug}`,
    publisher: {
      '@type': 'Organization',
      name: '暗网91安全指南',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.anwang91.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category?.name || '分类',
        item: `https://www.anwang91.com/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.anwang91.com/article/${slug}`}
        ogType="article"
        schemaJson={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <Navbar />
      <main className="pt-16">
        <article className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '分类', href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <header className="py-8 md:py-10">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors duration-200"
                >
                  {category?.name || article.category}
                </Link>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </span>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </span>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line"></i>
                  </span>
                  阅读约{article.readTime}分钟
                </span>
              </div>
            </header>

            <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
              <img
                src={articleImageUrls[article.slug] || ''}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none mb-12 text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <style>{`
              .article-content h2 {
                font-size: 1.5rem;
                font-weight: 700;
                margin-top: 2rem;
                margin-bottom: 0.75rem;
                color: oklch(var(--foreground-900));
                font-family: var(--font-heading);
              }
              .article-content h3 {
                font-size: 1.2rem;
                font-weight: 600;
                margin-top: 1.5rem;
                margin-bottom: 0.5rem;
                color: oklch(var(--foreground-800));
                font-family: var(--font-heading);
              }
              .article-content p {
                margin-bottom: 1rem;
                line-height: 1.75;
                color: oklch(var(--foreground-700));
              }
              .article-content strong {
                color: oklch(var(--foreground-900));
                font-weight: 600;
              }
            `}</style>

            <div className="border-t border-background-200/70 pt-8 mb-12">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((relArticle) => (
                  <ArticleCard
                    key={relArticle.id}
                    slug={relArticle.slug}
                    title={relArticle.title}
                    excerpt={relArticle.excerpt}
                    category={relArticle.category}
                    categoryName={category?.name || relArticle.category}
                    author={relArticle.author}
                    publishDate={relArticle.publishDate}
                    readTime={relArticle.readTime}
                    imageUrl={articleImageUrls[relArticle.slug] || ''}
                    tags={relArticle.tags}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}